import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DatalayerAssetExtendedDto,
  EnumVideoProcessor,
  videoProcessorToDisplay,
} from '@/types/datalayer-asset';
import { CameraIcon, Cctv, Plug, Volume2, Zap, MoreVertical, Trash2, ArrowRightLeft, ScanHeart, AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CamSneakPreview from './CamSneakPreview';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { SiteSelector } from '@/features/Devices/DevicePage/components/SiteSelector';
import { SitesSearchDto } from '@/app/Sites/siteApi';
import { MoveDeviceMut, DeleteDeviceMut } from '@/lib/api/devices/devices';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { LiveTesterDialog } from '@/app/Devices/components/LiveTesterDialog';

export default function SiteDeviceCard({ device, siteId }: { device: DatalayerAssetExtendedDto; siteId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [moveOpen, setMoveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [liveTesterOpen, setLiveTesterOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<SitesSearchDto | null>(null);

  const VPC = device.videoProcessorConfigs.length > 0 ? device.videoProcessorConfigs[0] : null;
  const isIntegrationHub =
    Boolean(device.integrationId) || VPC?.videoProcessor === EnumVideoProcessor.IntegrationHub;
  const hasComponents = !!device.relays?.length || !!device.speakers?.length;
  const iHubDevice = isIntegrationHub || hasComponents;

  const speakerNvrId = device.speakers?.[0]?.nvrId;
  const relayNvrId = device.relays?.[0]?.nvrId;
  const vpcNvrId = device.videoProcessorConfigs.find((x) => x?.nvrId)?.nvrId;
  const nvrId = device.nvrId || speakerNvrId || relayNvrId || vpcNvrId;
  const deviceLink = nvrId ? `/devices/${nvrId}/edit` : undefined;

  const isNonIHubWithSpeakers = !isIntegrationHub && !!device.speakers?.length;
  const isNonIHubWithRelays = !isIntegrationHub && !!device.relays?.length;

  const integrationName =
    device.speakers?.[0]?.integrationName ||
    device.relays?.[0]?.integrationName ||
    (VPC?.videoProcessor ? videoProcessorToDisplay[VPC.videoProcessor] : 'N/A');

  const deleteMut = useMutation(DeleteDeviceMut(nvrId ?? 'noop'));
  const moveMut = useMutation(MoveDeviceMut(nvrId ?? 'noop'));

  const handleDelete = async () => {
    if (!nvrId) return;
    try {
      await deleteMut.mutateAsync({
        nvr: {
          externalId: device.externalId,
          name: device.name,
        },
        monitoredDevices: [],
        integrationId: device.integrationId,
        customerId: device.companyId,
      });
      toast({ title: 'Device deleted' });
      queryClient.invalidateQueries({ queryKey: ['siteAssets'] });
      setDeleteOpen(false);
    } catch {
      toast({ title: 'Error deleting device', variant: 'destructive' });
    }
  };

  const handleMove = async () => {
    if (!selectedSite || !nvrId) return;
    try {
      await moveMut.mutateAsync({ targetProjectId: selectedSite.id });
      toast({ title: 'Device moved successfully', description: `Moved to ${selectedSite.name}` });
      queryClient.invalidateQueries({ queryKey: ['siteAssets'] });
      setMoveOpen(false);
      setSelectedSite(null);
    } catch {
      toast({ title: 'Error moving device', variant: 'destructive' });
    }
  };

  const cardContent = (
    <div className="relative rounded-lg border border-gray-700 bg-[#111] transition-colors hover:border-lime">
      <AspectRatio ratio={16 / 9}>
        <div className="flex h-full items-start justify-between border-b p-4">
          <div className="z-10">
            <h2 className="text-xl font-semibold text-lime">{device.name}</h2>
            <p className="text-gray-400">{VPC?.description}</p>
            <p className="text-gray-400">{device?.groupName}</p>
          </div>
          <div className="z-10 shrink-0">
            {!iHubDevice && (
              <div className="rounded-full border bg-gray-900/75 px-4 py-1 text-gray-400">
                V1 Device
              </div>
            )}
          </div>
        </div>
      </AspectRatio>
      {isIntegrationHub && (
        <CamSneakPreview VPCs={device.videoProcessorConfigs.map((x) => x.id)} />
      )}
      {!isIntegrationHub && isNonIHubWithSpeakers && (
        <div className="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center text-gray-600">
          <Volume2 className="h-12 w-12 text-gray-500 opacity-50" />
        </div>
      )}
      {!isIntegrationHub && isNonIHubWithRelays && !isNonIHubWithSpeakers && (
        <div className="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center text-gray-600">
          <Zap className="h-12 w-12 text-gray-500 opacity-50" />
        </div>
      )}
      {!isIntegrationHub && !isNonIHubWithSpeakers && !isNonIHubWithRelays && (
        <div className="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center text-gray-600">
          <Cctv className="m-auto h-full w-full p-16" color="currentcolor" />
        </div>
      )}
      <div className="flex text-sm text-gray-400">
        <div className="flex flex-1 items-center justify-center gap-2 border-r p-4">
          <CameraIcon className="h-4 w-4 text-blue-500" />
          <span>{device.videoProcessorConfigs.length}</span>
        </div>
        <div className="flex flex-1 items-center justify-center gap-2 border-r p-4">
          <Volume2 className="h-4 w-4 text-green-500" />
          <span>{device.speakers?.length || 0}</span>
        </div>
        <div className="flex flex-1 items-center justify-center gap-2 border-r p-4">
          <Zap className="h-4 w-4 text-yellow-500" />
          <span>{device.relays?.length || 0}</span>
        </div>
        <div className="flex flex-2 items-center justify-center gap-2 p-4">
          <Plug className="h-4 w-4" />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help truncate">
                  {integrationName.length > 11
                    ? integrationName.substring(0, 8) + '...'
                    : integrationName}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{integrationName}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );

  if (!iHubDevice) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="block cursor-not-allowed opacity-75">{cardContent}</div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Please use V1 to access this device</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <>
      <div className="relative">
        <Link to={deviceLink || '#'} className="block">
          {cardContent}
        </Link>

        {nvrId && (
          <div
            className="absolute right-2 top-2 z-20"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 bg-black/50 hover:bg-black/30 text-white hover:text-lime cursor-pointer"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    const firstCameraId = device.videoProcessorConfigs[0]?.id;
                    if (firstCameraId && nvrId) {
                      setLiveTesterOpen(true);
                    } else {
                      navigate(`/devices/${nvrId}/edit?tab=cameras`);
                    }
                  }}
                >
                  <ScanHeart className="mr-2 h-4 w-4" />
                  Live Test
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setMoveOpen(true);
                  }}
                >
                  <ArrowRightLeft className="mr-2 h-4 w-4" />
                  Move Device
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-red-400 focus:text-red-400"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteOpen(true);
                  }}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* Move Device Dialog */}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Move Device to Different Site</DialogTitle>
            <DialogDescription className="sr-only">Select a new site to move this device.</DialogDescription>
          </DialogHeader>
            <div className="px-6 pb-6 text-sm">
              {/* Warning info section */}
              <div className="border p-4 rounded mb-6">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 shrink-0 text-yellow-500" />
                  <p className="text-yellow-500 text-lg">Warning</p>
                </div>
                <p>Moving the device to a new site will:</p>
                <ul className="ml-5 list-disc space-y-1">
                  <li>Move all cameras, speakers, and relays on this device to the new site</li>
                  <li>Update the associated customer to the new site</li>
                </ul>
              </div>
            </div>

            {/* Form section */}
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="site-selector" className="text-lg">
                  Destination Site
                </Label>
                <SiteSelector
                  value={selectedSite?.id}
                  onChange={setSelectedSite}
                  excludeProjectId={siteId}
                />
              </div>
              </div>
            </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleMove} disabled={!selectedSite || moveMut.isPending}>
              {moveMut.isPending ? 'Moving...' : 'Move Device'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you absolutely sure?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the device and all its
              data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col-reverse sm:flex-col-reverse gap-2">
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMut.isPending}
              className="w-full"
            >
              {deleteMut.isPending ? 'Deleting...' : 'Delete Device'}
            </Button>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)} className="w-full">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Live Tester Dialog */}
      {nvrId && device.videoProcessorConfigs[0]?.id && (
        <LiveTesterDialog
          open={liveTesterOpen}
          onOpenChange={setLiveTesterOpen}
          deviceId={nvrId}
          cameraId={device.videoProcessorConfigs[0].id}
        />
      )}
    </>
  );
}
